import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Card,
  CardActionArea,
  cardClasses,
  CardProps,
  Grid,
  ImageListItemBar,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import classNames from 'classnames';

import { Icon, IconBadge, ImageWithSkeleton, Menu } from '@components/base';
import useImageGroupData from '@hooks/imageHooks/useImageGroupData';
import { ImageContext } from '@hooks/imageHooks/useImageGroupData/context';
import { ImageHeaders } from '@hooks/imageHooks/useImageHeaders';
import useConfirmDialog from '@hooks/useConfirmDialog';
import { ImageType } from '@models/Image';

import DownloadImageButton from './DownloadImageButton';
import DownloadImageMenuItem from './DownloadImageMenuItem';

const StyledCard = styled(Card)`
  height: 100%;
  width: 100%;
`;

const ImageGridItem = styled(Grid)`
  display: flex;
  flex-direction: column;
  height: 100%;

  .${cardClasses.root} {
    &.mute {
      opacity: 0.5;
      transition: 250ms;

      &:hover,
      &:focus {
        opacity: 1;
      }
    }
  }
`;

const StyledImageListItemBar = styled(ImageListItemBar)`
  padding-top: ${({ theme }) => theme.spacing(0.5)};
`;

export type ImageDisplayProps = {
  context?: ImageContext;
  deleteImage?: VoidFunction;
  ImageCardProps?: CardProps;
  imageMetadata: ImageHeaders;
  imageType: ImageType;
  /**
   * If `true`, images will be displayed without info/options, i.e. in `Tooltip` image previews
   */
  minified?: boolean;
  onClick?: () => void;
};

const widthRatioMap: Record<ImageType, number> = {
  poster: 2 * 3,
  square: 1 * 9,
  wide: 16,
};

const totalWidth = Object.values(widthRatioMap).reduce((acc, cur) => acc + cur, 0);

const ImageDisplay: FC<ImageDisplayProps> = ({
  ImageCardProps: imageCardProps,
  context,
  deleteImage,
  imageMetadata,
  imageType,
  minified,
  onClick,
}) => {
  const { t } = useTranslation();
  const { disableDelete, isDeleting } = useImageGroupData();
  const { filenameBase, defaultImageType, hasContextImages } = useImageGroupData();
  const { dialog, request } = useConfirmDialog();
  const { getSrc, isDeletable, isFallback, originalHeight, originalWidth } = imageMetadata || {};

  const widthFactor = widthRatioMap[imageType] / totalWidth;
  const src = getSrc(widthFactor * totalWidth * 40);
  const imageLabel = t('imageType', { context: imageType });

  const onDelete = deleteImage
    ? async () => {
        if (
          await request(
            t('deleteItem.confirm.heading', { type: t('contentType.image').toLocaleLowerCase() }),
            t('deleteItem.confirm', { type: t('contentType.image').toLocaleLowerCase() }),
            'yes'
          )
        )
          deleteImage();
      }
    : undefined;

  const isHighlighted =
    defaultImageType === imageType &&
    (!context || context === 'uploaded' || (context === 'default' && !hasContextImages));

  const isMuted =
    (defaultImageType && defaultImageType !== imageType) ||
    (context === 'default' && hasContextImages);

  const altText = t('images.alt', {
    imageType: t('imageType', { context: imageType }),
  });

  const image = (
    <ImageWithSkeleton
      alt={altText}
      containImage
      disableTitle={isMuted || isHighlighted}
      ratio={imageType}
      src={src}
    />
  );

  const imageComponent = (
    <StyledCard
      className={classNames({ highlight: isHighlighted, mute: isMuted })}
      {...imageCardProps}
      square
      variant='outlined'
    >
      {onClick ? (
        <CardActionArea
          onClick={e => {
            e.stopPropagation();
            onClick();
          }}
        >
          {image}
        </CardActionArea>
      ) : (
        image
      )}
    </StyledCard>
  );

  return (
    <Fragment>
      <ImageGridItem flex={widthFactor} size='auto' width={`${widthFactor * 100}%`}>
        {isHighlighted ? (
          <Tooltip
            arrow={false}
            followCursor
            title={t('images.default.current', { context: isFallback ? 'fallback' : undefined })}
          >
            <IconBadge
              color='warning'
              icon={isFallback ? 'warning' : 'star'}
              transparentBackground={!isFallback}
            >
              {imageComponent}
            </IconBadge>
          </Tooltip>
        ) : isMuted ? (
          <Tooltip
            arrow={false}
            followCursor
            title={
              <Trans
                components={[<strong />]}
                i18nKey={
                  imageType === defaultImageType
                    ? 'images.default.overwritten'
                    : 'images.default.missing'
                }
                values={{ imageType: defaultImageType }}
              />
            }
          >
            {imageComponent}
          </Tooltip>
        ) : (
          imageComponent
        )}

        {!minified && (
          <StyledImageListItemBar
            actionIcon={
              !disableDelete && deleteImage && isDeletable ? (
                <Menu>
                  <Tooltip title={t('options')}>
                    <Menu.IconButton edge='end'>
                      <Icon name='moreVert' />
                    </Menu.IconButton>
                  </Tooltip>

                  <Menu.Content>
                    <DownloadImageMenuItem
                      fileNameBase={filenameBase || ''}
                      imageType={imageType}
                      src={src}
                    />

                    <Menu.Item
                      disabled={!deleteImage}
                      icon={<Icon color='error' name='close' />}
                      keepMounted
                      loading={isDeleting(imageType)}
                      onClick={onDelete}
                    >
                      {t('delete')}
                    </Menu.Item>
                  </Menu.Content>
                </Menu>
              ) : (
                !isFallback && (
                  <DownloadImageButton
                    fileNameBase={filenameBase || ''}
                    imageType={imageType}
                    src={src}
                  />
                )
              )
            }
            position='below'
            subtitle={
              <Typography color='text.secondary' variant='inherit'>
                {isFallback
                  ? t('image.fallback')
                  : (originalWidth && originalHeight && `${originalWidth}x${originalHeight}`) ||
                    ' '}
              </Typography>
            }
            title={imageLabel}
          />
        )}
      </ImageGridItem>

      {dialog}
    </Fragment>
  );
};

export default ImageDisplay;
